import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  FileTextOutlined,
  LockOutlined,
  MailOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Button, Empty, Input, Modal, Spin, Tag, Tooltip } from 'antd';
import { useProfile } from '../../profile/components/ProfileContext';
import { UtilService } from '../../../../../core/services/util.service';
import { StagesService } from '../services/stages.service';
import { TemplatesService } from '../services/templates.service';
import {
  StageWithTemplates,
  Template,
  TemplateId,
} from '../models/templates.model';
import { route } from '../../../../../constants/routes';
import './HiringStages.css';

const emptyStage = () => new StageWithTemplates();
const emptyTemplate = () => new Template();

const templateIsDefault = (template?: TemplateId & { default?: boolean }) =>
  Boolean(template?.default || template?.isDefault);

const stageIsDefault = (stage?: StageWithTemplates & { default?: boolean }) =>
  Boolean(stage?.default || stage?.isDefault || stage?.defaultStage);

const hasContent = (html: string) =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0;

const StageScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, isAdmin } = useProfile();
  const { clientId, stageId } = useParams();
  const clientName = location.state?.clientName || 'Client';
  const quillRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [stage, setStage] = useState<StageWithTemplates>(emptyStage);
  const [templates, setTemplates] = useState<TemplateId[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [templateDraft, setTemplateDraft] = useState<Template>(emptyTemplate);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [placeholders, setPlaceholders] = useState<string[]>([]);

  const goBack = () => {
    navigate(route(`/client-profile/${clientId}`), {
      state: { activeTab: '2', clientName },
    });
  };

  const loadStage = useCallback(
    async (preferredTemplateId?: string | null) => {
      if (!companyId || !clientId || !stageId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const stageData = await StagesService.GetById(
          companyId,
          clientId,
          stageId
        );
        const mappedTemplates = (stageData.notificationTemplates || []).map(
          (item: any) => ({
            ...item,
            isDefault: Boolean(item.default || item.isDefault),
            createdDate: item.createdDate?.split(' ')[0] || '',
          })
        );
        const mappedStage = {
          ...stageData,
          isDefault: Boolean(
            (stageData as any).default ||
              stageData.isDefault ||
              stageData.defaultStage
          ),
          notificationTemplates: mappedTemplates,
        };
        setStage(mappedStage);
        setTemplates(mappedTemplates);
        const nextSelected =
          mappedTemplates.find((item) => item.id === preferredTemplateId) ||
          mappedTemplates[0];
        if (nextSelected) {
          setSelectedTemplateId(nextSelected.id);
          setTemplateDraft(
            new Template(nextSelected.name, nextSelected.template)
          );
          setCreatingTemplate(false);
        } else {
          setSelectedTemplateId(null);
          setTemplateDraft(emptyTemplate());
          setCreatingTemplate(false);
        }
      } catch (error) {
        console.error('Error fetching stage and templates:', error);
        UtilService.Alert(
          'Stage unavailable',
          'error',
          'This hiring stage could not be loaded.'
        );
      } finally {
        setLoading(false);
      }
    },
    [clientId, companyId, stageId]
  );

  useEffect(() => {
    void loadStage();
  }, [loadStage]);

  useEffect(() => {
    if (!isAdmin) return;
    void TemplatesService.GetPlaceholders()
      .then((items) => setPlaceholders(items || []))
      .catch((error) => {
        console.error('Failed to load template placeholders:', error);
        setPlaceholders([]);
      });
  }, [isAdmin]);

  const selectedTemplate = useMemo(
    () => templates.find((item) => item.id === selectedTemplateId),
    [selectedTemplateId, templates]
  );
  const protectedTemplate = templateIsDefault(
    selectedTemplate as TemplateId & { default?: boolean }
  );
  const templateDirty = selectedTemplate
    ? templateDraft.name.trim() !== selectedTemplate.name.trim() ||
      templateDraft.template !== selectedTemplate.template
    : creatingTemplate &&
      (Boolean(templateDraft.name.trim()) ||
        hasContent(templateDraft.template));

  const selectTemplate = (template: TemplateId) => {
    if (savingTemplate || deletingTemplate) return;
    setSelectedTemplateId(template.id);
    setTemplateDraft(new Template(template.name, template.template));
    setCreatingTemplate(false);
  };

  const startTemplate = () => {
    setSelectedTemplateId(null);
    setTemplateDraft(emptyTemplate());
    setCreatingTemplate(true);
  };

  const saveTemplate = async () => {
    if (
      !stageId ||
      !templateDraft.name.trim() ||
      !hasContent(templateDraft.template) ||
      savingTemplate
    ) {
      return;
    }
    setSavingTemplate(true);
    try {
      const payload = new Template(
        templateDraft.name.trim(),
        templateDraft.template
      );
      if (selectedTemplateId) {
        await TemplatesService.Patch(stageId, selectedTemplateId, payload);
        await loadStage(selectedTemplateId);
      } else {
        const updatedStage = await TemplatesService.Post(stageId, payload);
        const createdTemplate = [...(updatedStage.notificationTemplates || [])]
          .reverse()
          .find((item) => item.name === payload.name);
        await loadStage(createdTemplate?.id);
      }
      UtilService.Alert(
        selectedTemplateId ? 'Template updated' : 'Template created',
        'success',
        'The reusable candidate message is ready for recruiters.'
      );
    } catch (error) {
      console.error('Failed to save notification template:', error);
      UtilService.Alert(
        'Template not saved',
        'error',
        'Add a unique name and message, then try again.'
      );
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = () => {
    if (!stageId || !selectedTemplateId || protectedTemplate) return;
    Modal.confirm({
      title: `Delete “${selectedTemplate?.name}”?`,
      content:
        'Recruiters will no longer be able to select this message. Default templates are always protected.',
      okText: 'Delete template',
      okType: 'danger',
      cancelText: 'Keep template',
      onOk: async () => {
        setDeletingTemplate(true);
        try {
          await TemplatesService.Delete(stageId, selectedTemplateId);
          await loadStage();
          UtilService.Alert(
            'Template deleted',
            'success',
            'The custom message template was removed.'
          );
        } catch (error) {
          console.error('Failed to delete notification template:', error);
          UtilService.Alert(
            'Template not deleted',
            'error',
            'Default templates cannot be deleted.'
          );
        } finally {
          setDeletingTemplate(false);
        }
      },
    });
  };

  const insertPlaceholder = (placeholder: string) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      setTemplateDraft((current) => ({
        ...current,
        template: `${current.template}${placeholder}`,
      }));
      return;
    }
    const selection = editor.getSelection(true);
    const index = selection?.index ?? editor.getLength() - 1;
    editor.insertText(index, placeholder, 'user');
    editor.setSelection(index + placeholder.length, 0);
  };

  if (loading) {
    return (
      <main
        className="stage-workspace stage-workspace--loading"
        role="status"
        aria-label="Loading stage templates"
      >
        <Spin size="large" />
        <span>Loading stage and message templates…</span>
      </main>
    );
  }

  return (
    <main className="stage-workspace">
      <header className="stage-workspace__header">
        <button type="button" onClick={goBack}>
          <ArrowLeftOutlined aria-hidden />
          Hiring stages
        </button>
        <div className="stage-workspace__heading">
          <div>
            <span>{clientName} · Candidate workflow</span>
            <div>
              <h1>{stage.name || 'Hiring stage'}</h1>
              <Tag
                icon={
                  stageIsDefault(
                    stage as StageWithTemplates & { default?: boolean }
                  ) ? (
                    <LockOutlined />
                  ) : undefined
                }
                color={
                  stageIsDefault(
                    stage as StageWithTemplates & { default?: boolean }
                  )
                    ? 'blue'
                    : 'default'
                }
              >
                {stageIsDefault(
                  stage as StageWithTemplates & { default?: boolean }
                )
                  ? 'Protected default'
                  : 'Custom stage'}
              </Tag>
            </div>
            <p>
              Review the fixed stage details and maintain reusable candidate
              messages for this step.
            </p>
          </div>
        </div>
      </header>

      <section
        className="stage-details stage-details--readonly"
        aria-labelledby="stage-details-title"
      >
        <div className="stage-details__title">
          <span className="stage-details__icon">
            <LockOutlined aria-hidden />
          </span>
          <div>
            <span>Workflow identity</span>
            <h2 id="stage-details-title">Stage overview</h2>
            <p>
              The stage identity is protected; communication templates remain
              configurable below.
            </p>
          </div>
        </div>
        <dl className="stage-details__summary">
          <div>
            <dt>Stage name</dt>
            <dd>{stage.name || 'Hiring stage'}</dd>
          </div>
          <div>
            <dt>Recruiter guidance</dt>
            <dd>
              {stage.description ||
                'No additional guidance is configured for this stage.'}
            </dd>
          </div>
          <div>
            <dt>Templates</dt>
            <dd>
              {templates.length}{' '}
              {templates.length === 1
                ? 'message template'
                : 'message templates'}
            </dd>
          </div>
        </dl>
        <p className="stage-details__protection">
          <LockOutlined aria-hidden /> Name and guidance are locked to keep the
          hiring workflow consistent.
        </p>
      </section>

      <section
        className="template-workspace"
        aria-labelledby="template-workspace-title"
      >
        <aside className="template-workspace__sidebar">
          <div className="template-workspace__sidebar-header">
            <div>
              <span>Candidate communication</span>
              <h2 id="template-workspace-title">Message templates</h2>
            </div>
            {isAdmin ? (
              <Tooltip title="Add a reusable message">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<PlusOutlined />}
                  aria-label="Add message template"
                  onClick={startTemplate}
                />
              </Tooltip>
            ) : null}
          </div>
          <p>
            Select the starting message recruiters can personalize when moving a
            candidate here.
          </p>
          <div className="template-workspace__template-list">
            {templates.length ? (
              templates.map((item) => {
                const selected = item.id === selectedTemplateId;
                const isDefault = templateIsDefault(
                  item as TemplateId & { default?: boolean }
                );
                return (
                  <button
                    type="button"
                    key={item.id}
                    className={selected ? 'is-selected' : undefined}
                    aria-pressed={selected}
                    onClick={() => selectTemplate(item)}
                  >
                    <span className="template-workspace__template-icon">
                      <MailOutlined aria-hidden />
                    </span>
                    <span>
                      <strong>{item.name}</strong>
                      <small>
                        {isDefault ? 'Default · protected' : 'Custom template'}
                      </small>
                    </span>
                    {isDefault ? <LockOutlined aria-label="Protected" /> : null}
                  </button>
                );
              })
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No templates yet"
              />
            )}
          </div>
        </aside>

        <div className="template-editor">
          {creatingTemplate || selectedTemplate ? (
            <>
              <header className="template-editor__header">
                <div>
                  <span>
                    <FileTextOutlined aria-hidden />
                    {creatingTemplate ? 'New template' : 'Template editor'}
                  </span>
                  <h2>
                    {creatingTemplate
                      ? 'Create a candidate message'
                      : selectedTemplate?.name}
                  </h2>
                </div>
                {protectedTemplate ? (
                  <Tag icon={<LockOutlined />} color="blue">
                    Default · cannot delete
                  </Tag>
                ) : null}
              </header>

              <label className="template-editor__name" htmlFor="template-name">
                Template name
                <Input
                  id="template-name"
                  value={templateDraft.name}
                  maxLength={75}
                  showCount
                  disabled={!isAdmin}
                  placeholder="Example: Schedule technical interview"
                  onChange={(event) =>
                    setTemplateDraft((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                />
              </label>

              {isAdmin && placeholders.length ? (
                <div className="template-editor__placeholders">
                  <span>Insert candidate data</span>
                  <div>
                    {placeholders.map((placeholder) => (
                      <button
                        type="button"
                        key={placeholder}
                        onClick={() => insertPlaceholder(placeholder)}
                      >
                        {placeholder}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="template-editor__message">
                <span>Message</span>
                <ReactQuill
                  ref={quillRef}
                  value={templateDraft.template}
                  readOnly={!isAdmin}
                  placeholder="Write the reusable message recruiters can personalize…"
                  theme="snow"
                  onChange={(value: string) =>
                    setTemplateDraft((current) => ({
                      ...current,
                      template: value,
                    }))
                  }
                  modules={{
                    toolbar: isAdmin
                      ? [
                          ['bold', 'italic', 'underline'],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          ['link'],
                          ['clean'],
                        ]
                      : false,
                  }}
                />
              </div>

              {isAdmin ? (
                <footer className="template-editor__actions">
                  {!creatingTemplate && !protectedTemplate ? (
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      loading={deletingTemplate}
                      onClick={deleteTemplate}
                    >
                      Delete custom template
                    </Button>
                  ) : (
                    <span className="template-editor__protected-note">
                      {protectedTemplate ? (
                        <>
                          <LockOutlined aria-hidden /> Default templates can be
                          edited but never deleted.
                        </>
                      ) : null}
                    </span>
                  )}
                  <div>
                    {creatingTemplate ? (
                      <Button
                        onClick={() => {
                          setCreatingTemplate(false);
                          const fallback = templates[0];
                          if (fallback) selectTemplate(fallback);
                        }}
                      >
                        Cancel
                      </Button>
                    ) : null}
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      loading={savingTemplate}
                      disabled={
                        !templateDirty ||
                        !templateDraft.name.trim() ||
                        !hasContent(templateDraft.template)
                      }
                      onClick={() => void saveTemplate()}
                    >
                      {creatingTemplate ? 'Create template' : 'Save template'}
                    </Button>
                  </div>
                </footer>
              ) : null}
            </>
          ) : (
            <div className="template-editor__empty">
              <MailOutlined aria-hidden />
              <h2>No message templates yet</h2>
              <p>
                Add a reusable candidate message for recruiters to personalize
                at this stage.
              </p>
              {isAdmin ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={startTemplate}
                >
                  Create first template
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default StageScreen;
